import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { BoxProps, DialogProps } from '@mui/material';
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LogoIcon } from 'assets/icons/logos/starLogo105.svg';

import { Dialog, DialogActions, CloseButton, FieldInput, CancelBtn, CreateBtn } from './styled';

interface ConfirmDialogProps extends Omit<DialogProps, 'onSubmit'> {
  onClose?: () => void;
  onSubmit: (label: string) => void;
  isLoading?: boolean;
  fieldCount: number;
  subTitle?: string;
  children?: ReactNode;
  dialogSx?: BoxProps['sx'];
  titleSx?: BoxProps['sx'];
  contentSx?: BoxProps['sx'];
  withLogo?: boolean;
  isContentOverflow?: boolean;
}

export function AddFieldDialog({
  title,
  onClose,
  onSubmit,
  fieldCount,
  open,
  isLoading,
  subTitle,
  dialogSx,
  titleSx,
  children,
  withLogo = false,
  isContentOverflow,
  contentSx,
  ...otherProps
}: ConfirmDialogProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState(`New Field ${fieldCount + 1}`);
  const { t } = useTranslation();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Dialog open={open} sx={dialogSx} {...otherProps}>
      {withLogo && <LogoIcon style={{ margin: '0 auto' }} />}

      {subTitle && (
        <div>
          <CloseButton
            variant="text"
            startIcon={<KeyboardBackspaceRoundedIcon fontSize="large" />}
            onClick={onClose}
          >
            {subTitle}
          </CloseButton>
        </div>
      )}

      <FieldInput
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        ref={inputRef}
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <DialogActions>
        {onClose && (
          <CancelBtn variant="outlined" onClick={onClose}>
            {t('document.create.form.buttons.cancel')}
          </CancelBtn>
        )}
        <CreateBtn variant="contained" onClick={() => onSubmit(label)}>
          {t('document.create.form.buttons.create')}
        </CreateBtn>
      </DialogActions>
    </Dialog>
  );
}
